import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { IconButton } from '@flipster/react/design-system/icon-button';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import LockClosedIcon from '@flipster/react/icon/generated/LockClosedIcon';
import { Styles, css, sva } from '@flipster/styled-system/css';
import { grid, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { alert } from '@flipster/ui/Alert';
import { Usd1Currency, UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { DateFormatter, formatDatetime } from '@flipster/utils/datetime';
import Num, { formatToShortNumber, numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { competitionBunja } from '../../bunja';

export interface MilestoneTierData {
  prizePool: string;
  requiredTradingVolume: string;
}

const progressBarRecipe = sva({
  slots: ['progressBar'],
  base: {
    progressBar: {
      w: 'calc(100% - 2px)',
      bg: 'content.accent.info',
      rounded: 100,
      h: 6,
    },
  },
  variants: {
    isLocked: {
      true: {
        progressBar: {
          bg: 'background.default.level-4',
        },
      },
    },
  },
});

const ProgressBar = ({ isLocked, css: cssProps }: { isLocked: boolean; css?: Styles }) => {
  const style = progressBarRecipe.raw({ isLocked });
  return <div className={css(style.progressBar, cssProps)} />;
};

const tierColumnReciepe = sva({
  slots: ['icon', 'prize', 'volume'],
  base: {
    icon: {
      rounded: 9999,
      w: 28,
      h: 28,
      m: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    prize: {
      textStyle: 'Text/S/Regular',
      color: 'content.default.level-3',
    },
    volume: {
      textStyle: 'Text/S/Regular',
      color: 'content.default.level-3',
    },
  },
  variants: {
    isLocked: {
      true: {
        icon: {
          bg: 'background.default.level-4',
        },
      },
    },
    isCurrent: {
      true: {
        prize: {
          textStyle: 'Text/S/Strong',
          color: 'content.default.level-5',
        },
        volume: {
          textStyle: 'Text/S/Strong',
          color: 'content.default.level-5',
        },
      },
    },
  },
});

interface TierColumnProps {
  isLocked: boolean;
  isCurrent: boolean;
  prize: string;
  volume: string;
}
const TierColumn = ({ isLocked, isCurrent, prize, volume }: TierColumnProps) => {
  const css = tierColumnReciepe({ isLocked, isCurrent });
  return (
    <div className={vstack({ gap: 2 })}>
      <div className={css.prize}>{prize}</div>
      {isLocked === true ? (
        <div className={css.icon}>
          <LockClosedIcon size={14} />
        </div>
      ) : (
        <CheckCircleIcon color={token('colors.content.accent.info')} size={28} />
      )}
      <div className={css.volume}>{volume}</div>
    </div>
  );
};

const TitleSection = ({ prizePool }: { prizePool: string }) => {
  const { id } = useBunja(competitionBunja);

  return (
    <div className={stack({ gap: 5, alignItems: 'flex-start' })}>
      <div className={hstack({ gap: 2 })}>
        <p className={css({ textStyle: 'Title/S', color: 'content.accent.info' })}>
          <FormattedMessage
            id='Autogen_common.r251126.s_Up_to_numberFormatter_USDT_prize_pool'
            values={{
              numberFormatter: numberFormatter(prizePool, { precision: UsdtPrecision }),
            }}
          />
        </p>

        <IconButton
          icon={<InformationCircleIcon />}
          hierarchy='quaternary'
          size='small'
          disabled={false}
          onClick={() => {
            segment.track(CompetitionsEvent.CompetitionRankingPageInfoClick, { competition_id: id });
            alert.show({
              title: 'Prize pool rules',
              message: `Prizes change with total trading volume.
                • Higher volume = larger pool
                • Lower volume = smaller pool (milestones may decrease)
                If you leave the competition, your trades are removed.`,
              confirmText: 'Got it',
            });
          }}
        />
      </div>
      <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Trade_more_to_reach_the_next_milestone' />
      </p>
    </div>
  );
};

const StatsSection = ({
  currentPrizePool,
  totalTradingVolume,
}: {
  currentPrizePool?: string;
  totalTradingVolume?: string;
}) => {
  return (
    <div className={hstack({ gap: 8, mt: 24 })}>
      <div className={stack({ gap: 4, flex: 1 })}>
        <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-1' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Current_prize_USDT' />
        </p>
        <p className={css({ textStyle: 'Title/S/Monospace', color: 'content.default.level-5' })}>
          {numberFormatter(currentPrizePool || '0', { precision: UsdtPrecision })}
        </p>
      </div>
      <div className={stack({ gap: 4, flex: 1 })}>
        <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-1' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Total_volume_USDT' />
        </p>
        <p className={css({ textStyle: 'Title/S/Monospace', color: 'content.default.level-5' })}>
          {numberFormatter(totalTradingVolume || '0', { precision: UsdtPrecision, toFixed: true })}
        </p>
      </div>
    </div>
  );
};

interface MilestoneTierItemProps {
  tier: MilestoneTierData;
  currentPrizeUsdt: string;
  nextTier?: MilestoneTierData;
  isLastInRow: boolean;
}
const MilestoneTierItem = ({ tier, currentPrizeUsdt, nextTier, isLastInRow }: MilestoneTierItemProps) => {
  const prizeLabel = formatToShortNumber(tier.prizePool);
  const volumeLabel = formatToShortNumber(tier.requiredTradingVolume);
  const isLocked = Num(currentPrizeUsdt).lt(Num(tier.prizePool));
  const isProgressLocked = nextTier ? Num(currentPrizeUsdt).lt(Num(nextTier.prizePool)) : false;
  const isCurrent = !isLocked && (nextTier ? Num(currentPrizeUsdt).lt(Num(nextTier.prizePool)) : true);

  return (
    <div className={hstack({ gap: 2 })}>
      <TierColumn isLocked={isLocked} isCurrent={isCurrent} prize={prizeLabel} volume={volumeLabel} />
      {!isLastInRow && <ProgressBar isLocked={isProgressLocked} css={{ mr: 2 }} />}
    </div>
  );
};

type Milestone = {
  prizePool: string;
  requiredTradingVolume: string;
};
interface PrizeSectionProps {
  milestone?: Milestone[];
  currentPrizePool?: string;
}
const PrizeSection = ({ milestone, currentPrizePool }: PrizeSectionProps) => {
  const tiers = milestone?.slice().sort((a, b) => parseInt(a.prizePool) - parseInt(b.prizePool)) ?? []; // 상금 기준 정렬
  const currentPrizeUsdt = currentPrizePool || '0';

  if (tiers.length === 0) return null;

  const itemsPerRow = 5;
  const tierRows = Array.from({ length: Math.ceil(tiers.length / itemsPerRow) }, (_, i) =>
    tiers.slice(i * itemsPerRow, (i + 1) * itemsPerRow),
  );
  const maxColumns = Math.min(itemsPerRow, tiers.length);
  return (
    <div
      className={stack({
        gap: 12,
        p: 12,
        mt: 16,
        borderColor: 'border.default.level-2',
        borderWidth: '1px',
        borderStyle: 'solid',
        rounded: 8,
      })}
    >
      <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Prize' />
      </p>
      <div
        style={{
          gridTemplateColumns: `repeat(${maxColumns - 1}, 1fr) 28px`,
        }}
        className={grid({ rowGap: 8, columnGap: 2, alignItems: 'stretch' })}
      >
        {tierRows.map((rowTiers, rowIdx) => {
          return rowTiers.map((t, idx) => {
            const absoluteIdx = rowIdx * itemsPerRow + idx;
            return (
              <MilestoneTierItem
                key={absoluteIdx}
                tier={t}
                currentPrizeUsdt={currentPrizeUsdt}
                nextTier={tiers[absoluteIdx + 1]}
                isLastInRow={idx === rowTiers.length - 1}
              />
            );
          });
        })}
      </div>
      <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Volume' />
      </p>
    </div>
  );
};

const LastUpdatedSection = () => {
  return (
    <p className={css({ mt: 8, textStyle: 'Text/S/Regular', color: 'content.default.level-1' })}>
      <FormattedMessage
        id='Autogen_common.r251126.s_Last_updated_formatDatetime_UTC'
        values={{
          formatDatetime: formatDatetime(new Date(), { format: DateFormatter.EnMonthDateTime, isUTC: true }),
        }}
      />
    </p>
  );
};

export const MilestonePrize = () => {
  const { competitionAtom } = useBunja(competitionBunja);
  const competition = useAtomValue(competitionAtom);
  const prizePoolCurrency = competition?.competitionUrl === 'usd1-perps-trading-battle' ? Usd1Currency : UsdtCurrency;

  if (!competition?.milestone) return null;
  if (!competition.participationTime) {
    return (
      <div className={vstack({ gap: 4, color: 'content.default.level-5', p: 16, bgGradient: 'programs.promotion' })}>
        <p className={css({ textStyle: 'Text/M/Strong' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Total_prize_pool' />
        </p>
        <p className={css({ textStyle: 'Title/S/Monospace' })}>
          <FormattedMessage
            id='Autogen_common.r251126.s_Up_to_numberFormatter_prizePoolCurrency'
            values={{
              numberFormatter: numberFormatter(competition.prizePool, { precision: UsdtPrecision, toFixed: true }),
              prizePoolCurrency: prizePoolCurrency,
            }}
          />
        </p>
      </div>
    );
  }

  return (
    <div
      className={css({
        py: { base: 24, tablet: 32 },
        px: 16,
        bg: 'background.default.level-1',
      })}
    >
      <div
        className={css({
          tablet: {
            maxW: 'desktopMaxWidth',
            mx: 'auto',
          },
        })}
      >
        <TitleSection prizePool={competition.prizePool} />
        <StatsSection
          currentPrizePool={competition.currentMilestonePrizePool}
          totalTradingVolume={competition.totalTradingVolume}
        />
        <PrizeSection milestone={competition.milestone} currentPrizePool={competition.currentMilestonePrizePool} />
        <LastUpdatedSection />
      </div>
    </div>
  );
};
